import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Search, LayoutGrid, List, ChevronRight,
  Building2, DollarSign,
  Target, ArrowUpRight, Sprout,
  CheckCircle2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { GROWTH_PATH_STEPS, SHOOT_STATUSES, URGENCY_LEVELS } from '../config/growthPath';
import { API_URL } from '../config/api';

interface Shoot {
  id: string;
  title: string;
  company: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  source: string;
  status: string;
  industry: string;
  companySize: string;
  urgency: string;
  budget?: number;
  timeline?: string;
  priority: number;
  estimatedValue: number;
  createdAt: string;
  updatedAt: string;
  painPoints?: any[];
  currentWorkflows?: any[];
  discoverySessions?: any[];
}

async function fetchShoots(): Promise<Shoot[]> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_URL}/api/opportunities`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!res.ok) throw new Error('Failed to fetch shoots');
  return res.json();
}

async function createShoot(data: Partial<Shoot>): Promise<Shoot> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_URL}/api/opportunities`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error('Failed to create shoot');
  return res.json();
}

function getStatusBadge(status: string) {
  const statusConfig = SHOOT_STATUSES.find(s => s.value === status) || SHOOT_STATUSES[0];
  const colors: Record<string, string> = {
    gray: 'bg-gray-100 text-gray-700',
    blue: 'bg-blue-100 text-blue-700',
    purple: 'bg-purple-100 text-purple-700',
    amber: 'bg-amber-100 text-amber-700',
    orange: 'bg-orange-100 text-orange-700',
    pink: 'bg-pink-100 text-pink-700',
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700',
    slate: 'bg-slate-100 text-slate-700',
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${colors[statusConfig.color]}`}>
      {statusConfig.label}
    </span>
  );
}

function getUrgencyBadge(urgency: string) {
  const urgencyConfig = URGENCY_LEVELS.find(u => u.value === urgency);
  const colors: Record<string, string> = {
    low: 'bg-slate-100 text-slate-600',
    medium: 'bg-blue-100 text-blue-600',
    high: 'bg-amber-100 text-amber-700',
    critical: 'bg-red-100 text-red-700'
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[urgency] || colors.low}`}>
      {urgencyConfig?.label || urgency}
    </span>
  );
}

function GrowthPathProgress({ sessions, status }: { sessions?: any[], status: string }) {
  // Determine completed steps based on status and sessions
  const completedSteps = getCompletedStepsFromStatus(status);
  
  return (
    <div className="flex items-center gap-0.5">
      {GROWTH_PATH_STEPS.map((step, index) => {
        const isCompleted = completedSteps > index;
        const isCurrent = completedSteps === index;
        return (
          <div
            key={step.id}
            className={`w-6 h-1.5 rounded-full transition-colors ${
              isCompleted ? 'bg-green-500' : isCurrent ? 'bg-blue-500' : 'bg-gray-200'
            }`}
            title={`${step.cyvineName}`}
          />
        );
      })}
    </div>
  );
}

function getCompletedStepsFromStatus(status: string): number {
  const statusSteps: Record<string, number> = {
    'new': 0,
    'discovery_scheduled': 1,
    'in_discovery': 3,
    'proposal_pending': 5,
    'proposal_sent': 6,
    'negotiating': 6,
    'won': 7,
    'lost': 7,
    'paused': 0,
  };
  return statusSteps[status] || 0;
}

function CreateShootModal({ onClose, onCreated }: { onClose: () => void, onCreated: () => void }) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    title: '',
    company: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    source: 'referral',
    status: 'new',
    industry: '',
    companySize: 'small',
    urgency: 'medium',
    budget: '',
    timeline: '',
  });

  const createMutation = useMutation({
    mutationFn: createShoot,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shoots'] });
      onCreated();
      onClose();
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate({
      ...formData,
      budget: formData.budget ? parseInt(formData.budget) : undefined,
      estimatedValue: formData.budget ? parseInt(formData.budget) : 0,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Sprout className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">New Shoot</h2>
              <p className="text-sm text-gray-500">Plant a new opportunity to nurture</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Shoot Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  What's the opportunity?
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="e.g., Order Processing Automation"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company
                </label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={e => setFormData({ ...formData, company: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Company name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Industry
                </label>
                <input
                  type="text"
                  value={formData.industry}
                  onChange={e => setFormData({ ...formData, industry: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="e.g., E-commerce, Healthcare"
                  required
                />
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Contact</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Name
                </label>
                <input
                  type="text"
                  value={formData.contactName}
                  onChange={e => setFormData({ ...formData, contactName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Full name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.contactEmail}
                  onChange={e => setFormData({ ...formData, contactEmail: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="email@company.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone (optional)
                </label>
                <input
                  type="tel"
                  value={formData.contactPhone}
                  onChange={e => setFormData({ ...formData, contactPhone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="+1-555-0123"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Source
                </label>
                <select
                  value={formData.source}
                  onChange={e => setFormData({ ...formData, source: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="referral">Referral</option>
                  <option value="website">Website</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="email">Inbound Email</option>
                  <option value="voice_call">Phone Call</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>

          {/* Business Context */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Business Context</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Size
                </label>
                <select
                  value={formData.companySize}
                  onChange={e => setFormData({ ...formData, companySize: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="startup">Startup (&lt;10)</option>
                  <option value="small">Small (10-50)</option>
                  <option value="medium">Medium (50-200)</option>
                  <option value="large">Large (200+)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Urgency
                </label>
                <select
                  value={formData.urgency}
                  onChange={e => setFormData({ ...formData, urgency: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  {URGENCY_LEVELS.map(level => (
                    <option key={level.value} value={level.value}>{level.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Budget (optional)
                </label>
                <input
                  type="number"
                  value={formData.budget}
                  onChange={e => setFormData({ ...formData, budget: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Estimated budget"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <Sprout className="w-4 h-4" />
              {createMutation.isPending ? 'Creating...' : 'Plant Shoot'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Shoots() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>('list');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { data: shoots, isLoading, refetch } = useQuery({
    queryKey: ['shoots'],
    queryFn: fetchShoots
  });

  const filteredShoots = shoots?.filter(shoot => {
    const matchesSearch = 
      shoot.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shoot.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shoot.contactName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || shoot.status === statusFilter;
    return matchesSearch && matchesStatus;
  }) || [];

  // Group by status for kanban view
  const shootsByStatus = SHOOT_STATUSES.reduce((acc, status) => {
    acc[status.value] = filteredShoots.filter(s => s.status === status.value);
    return acc;
  }, {} as Record<string, Shoot[]>);

  // Calculate pipeline stats
  const pipelineValue = filteredShoots.reduce((sum, s) => sum + (s.estimatedValue || 0), 0);
  const activeCount = filteredShoots.filter(s => !['won', 'lost', 'paused'].includes(s.status)).length;
  const wonCount = filteredShoots.filter(s => s.status === 'won').length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Sprout className="w-7 h-7 text-green-600" />
            Shoots
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Opportunities growing through the 7-Step Growth Path
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Shoot
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Target className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Active Shoots</p>
              <p className="text-xl font-semibold text-gray-900">{activeCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Pipeline Value</p>
              <p className="text-xl font-semibold text-gray-900">
                ${pipelineValue.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Won This Period</p>
              <p className="text-xl font-semibold text-gray-900">{wonCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Growth Path Guide */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 p-4">
        <h3 className="text-sm font-medium text-green-800 mb-3 flex items-center gap-2">
          <span className="text-lg">🌱</span> The 7-Step Growth Path
        </h3>
        <div className="flex flex-wrap gap-2">
          {GROWTH_PATH_STEPS.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <span className="text-lg mr-1">{step.icon}</span>
              <span className="text-sm text-green-700">{step.cyvineName}</span>
              {index < GROWTH_PATH_STEPS.length - 1 && (
                <ChevronRight className="w-4 h-4 text-green-400 mx-1" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search shoots..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
        >
          <option value="all">All Statuses</option>
          {SHOOT_STATUSES.map(status => (
            <option key={status.value} value={status.value}>{status.label}</option>
          ))}
        </select>
        <div className="flex rounded-lg border border-gray-300 overflow-hidden">
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 ${viewMode === 'list' ? 'bg-green-100 text-green-700' : 'text-gray-500 hover:bg-gray-50'}`}
          >
            <List className="w-5 h-5" />
          </button>
          <button
            onClick={() => setViewMode('kanban')}
            className={`p-2 ${viewMode === 'kanban' ? 'bg-green-100 text-green-700' : 'text-gray-500 hover:bg-gray-50'}`}
          >
            <LayoutGrid className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* List View */}
      {viewMode === 'list' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Shoot</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Contact</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Status</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Growth Path</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Value</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Priority</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredShoots.map(shoot => (
                  <tr key={shoot.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4">
                      <Link to={`/shoots/${shoot.id}`} className="block group">
                        <div className="font-medium text-gray-900 group-hover:text-green-600 transition-colors">
                          {shoot.title}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center gap-1">
                          <Building2 className="w-3.5 h-3.5" />
                          {shoot.company}
                        </div>
                      </Link>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm">
                        <div className="text-gray-900">{shoot.contactName}</div>
                        <div className="text-gray-500">{shoot.contactEmail}</div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      {getStatusBadge(shoot.status)}
                    </td>
                    <td className="px-4 py-4">
                      <GrowthPathProgress sessions={shoot.discoverySessions} status={shoot.status} />
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        ${(shoot.estimatedValue || 0).toLocaleString()}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                          shoot.priority >= 8 ? 'bg-red-100 text-red-700' :
                          shoot.priority >= 6 ? 'bg-amber-100 text-amber-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {shoot.priority}
                        </div>
                        {getUrgencyBadge(shoot.urgency)}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <Link 
                        to={`/shoots/${shoot.id}`}
                        className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      >
                        <ArrowUpRight className="w-4 h-4" />
                      </Link>
                    </td>
                  </tr>
                ))}
                {filteredShoots.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                      <Sprout className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No shoots found</p>
                      <p className="text-sm">Create your first shoot to start growing opportunities</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Kanban View */}
      {viewMode === 'kanban' && (
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-4 min-w-max">
            {SHOOT_STATUSES.filter(s => !['won', 'lost'].includes(s.value)).map(status => (
              <div key={status.value} className="w-72 flex-shrink-0">
                <div className="bg-gray-100 rounded-lg p-3 mb-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-gray-900">{status.label}</h3>
                    <span className="text-sm text-gray-500">
                      {shootsByStatus[status.value]?.length || 0}
                    </span>
                  </div>
                </div>
                <div className="space-y-3">
                  {shootsByStatus[status.value]?.map(shoot => (
                    <Link
                      key={shoot.id}
                      to={`/shoots/${shoot.id}`}
                      className="block bg-white rounded-lg border border-gray-200 p-4 hover:border-green-300 hover:shadow-md transition-all"
                    >
                      <div className="font-medium text-gray-900 mb-1">{shoot.title}</div>
                      <div className="text-sm text-gray-500 mb-3 flex items-center gap-1">
                        <Building2 className="w-3.5 h-3.5" />
                        {shoot.company}
                      </div>
                      <GrowthPathProgress sessions={shoot.discoverySessions} status={shoot.status} />
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">
                          ${(shoot.estimatedValue || 0).toLocaleString()}
                        </span>
                        {getUrgencyBadge(shoot.urgency)}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateShootModal 
          onClose={() => setShowCreateModal(false)} 
          onCreated={() => refetch()} 
        />
      )}
    </div>
  );
}